import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import * as localizationMaps from '../../localization/maps.js';
import Text from '../../components/Text.js';
import Tooltip from '../../components/Tooltip.js';
import * as Theme from '../../theme/index.js';
import i18n from '../../localization/i18n.js';
import tokens from '../../localization/tokens.js';
import { helpers } from '@simplybenefits/sb-utils';

/**
 *
 * @param {*} ltd
 * @returns
 */
const createGradedLTDCoverage = ltd => {
  let ltdStr = i18n.t(tokens.labels.coverage.percentageFirstMaxOfMonthlyEarnings, {
    percentageFirstMax: ltd.percentageFirstMax,
    firstMax: helpers.numberWithCommas(ltd.firstMax)
  });
  if (ltd.percentageSecondMax && ltd.secondMax === 0) {
    ltdStr += i18n.t(tokens.labels.coverage.percentageSecondMaxOfRemainder, {
      percentageSecondMax: ltd.percentageSecondMax
    });
    return ltdStr;
  }
  if (ltd.percentageSecondMax && ltd.secondMax > 0) {
    ltdStr += i18n.t(tokens.labels.coverage.percentageSecondMaxOfNextSecondMax, {
      percentageSecondMax: ltd.percentageSecondMax,
      secondMax: helpers.numberWithCommas(ltd.secondMax)
    });
  }
  if (ltd.percentageThirdMax) {
    const thirdMaxStr = ltd.thirdMax > 0 ? i18n.t(tokens.labels.coverage.ofTheNext) : i18n.t(tokens.labels.coverage.theRemainder);
    const thirdMaxValue = ltd.thirdMax ? helpers.numberWithCommas(ltd.thirdMax) : '';
    ltdStr += i18n.t(tokens.labels.coverage.percentageThirdMaxOfThirdMax, {
      percentageThirdMax: ltd.percentageThirdMax,
      thirdMaxStr,
      thirdMaxValue
    });
    return ltdStr;
  }
  return ltdStr;
};

/**
 *
 * @param {*} std
 * @returns
 */
const createGradedSTDCoverage = std => {
  let stdStr = i18n.t(tokens.labels.coverage.gradedScaleFirstPart, {
    percentageFirstMax: std.percentageFirstMax,
    firstMax: helpers.numberWithCommas(std.firstMax)
  });
  if (std.percentageSecondMax && std.secondMax === 0) {
    stdStr += i18n.t(tokens.labels.coverage.gradedScaleSecondPart, {
      percentageSecondMax: std.percentageSecondMax
    });
    return stdStr;
  }
  if (std.percentageSecondMax && std.secondMax > 0) {
    stdStr += i18n.t(tokens.labels.coverage.gradedScaleThirdPart, {
      percentageSecondMax: std.percentageSecondMax,
      secondMax: std.secondMax
    });
  }
  if (std.percentageThirdMax) {
    const thirdMaxType = std.thirdMax > 0 ? i18n.t(tokens.labels.coverage.ofTheNext) : i18n.t(tokens.labels.coverage.theRemainder);
    const thirdMax = std.thirdMax ? helpers.numberWithCommas(std.thirdMax) : '';
    stdStr += i18n.t(tokens.labels.coverage.gradedScaleFourthPart, {
      percentageThirdMax: std.percentageThirdMax,
      thirdMaxType,
      thirdMax
    });
    return stdStr;
  }
  return stdStr;
};

/**
 * 
 * @param {*} param0 
 * @returns 
 */
function paramedicalOptions(data) {
  if (data.paramedicalCombinedMaximumSelected) {
    return /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.paramedicalCombinedMaximum, {
      paramedicalCombinedMaximum: helpers.formatNumber(data.paramedicalCombinedMaximum)
    }));
  }
  if (data.paramedicalPerPractitionerSelected && data.paramedicalApplyAmountToAllSelected && data.paramedicalMaximum > 0) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.acupuncturistMaximum, {
      acupuncturistMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.audiologistMaximum, {
      audiologistMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.chiropractorMaximum, {
      chiropractorMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.massageMaximum, {
      massageMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.naturopathMaximum, {
      naturopathMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.occupationalTherapistMaximum, {
      occupationalTherapistMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.physiotherapyMaximum, {
      physiotherapyMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.registeredDieticianMaximum, {
      registeredDieticianMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.speechTherapyMaximum, {
      speechTherapyMax: helpers.formatNumber(data.paramedicalMaximum)
    })), !data.paramedicalGroupMentalWellnessSelected ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.psychologistMaximum, {
      psychologistMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.psychotherapistMaximum, {
      psychotherapistMaximum: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.socialWorkerMaximum, {
      socialWorkerMax: helpers.formatNumber(data.paramedicalMaximum)
    }))) : null, !data.paramedicalGroupFunctionalTreatmentSelected ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.chiropodistMaximum, {
      chiropodistMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.osteopathMaximum, {
      osteopathMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.podiatristMaximum, {
      podiatristMax: helpers.formatNumber(data.paramedicalMaximum)
    }))) : null);
  }
  if (data.paramedicalPerPractitionerSelected && data.paramedicalSetPerPractitionerSelected) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.acupuncturistMaximum, {
      acupuncturistMax: helpers.formatNumber(data.acupuncturistMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.audiologistMaximum, {
      audiologistMax: helpers.formatNumber(data.audiologistMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.chiropractorMaximum, {
      chiropractorMax: helpers.formatNumber(data.chiropractorMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.massageMaximum, {
      massageMax: helpers.formatNumber(data.massageMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.naturopathMaximum, {
      naturopathMax: helpers.formatNumber(data.naturopathMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.occupationalTherapistMaximum, {
      occupationalTherapistMax: helpers.formatNumber(data.occupationalTherapistMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.physiotherapyMaximum, {
      physiotherapyMax: helpers.formatNumber(data.physiotherapyMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.registeredDieticianMaximum, {
      registeredDieticianMax: helpers.formatNumber(data.registeredDieticianMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.speechTherapyMaximum, {
      speechTherapyMax: helpers.formatNumber(data.speechTherapyMaximum)
    })), !data.paramedicalGroupMentalWellnessSelected ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.psychologistMaximum, {
      psychologistMax: helpers.formatNumber(data.psychologistMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.psychotherapistMaximum, {
      psychotherapistMaximum: helpers.formatNumber(data.psychotherapistMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.socialWorkerMaximum, {
      socialWorkerMax: helpers.formatNumber(data.socialWorkerMaximum)
    }))) : null, !data.paramedicalGroupFunctionalTreatmentSelected ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.chiropodistMaximum, {
      chiropodistMax: helpers.formatNumber(data.chiropodistMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.osteopathMaximum, {
      osteopathMax: helpers.formatNumber(data.osteopathMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.podiatristMaximum, {
      podiatristMax: helpers.formatNumber(data.podiatristMaximum)
    }))) : null);
  }
  if (data.paramedicalPerPractitionerToCombinedMaximumSelected && data.paramedicalApplyAmountToAllSelected && data.paramedicalMaximum > 0) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.paramedicalCombinedMaximum, {
      paramedicalCombinedMaximum: helpers.formatNumber(data.paramedicalCombinedMaximum, 2, false)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.acupuncturistMaximum, {
      acupuncturistMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.audiologistMaximum, {
      audiologistMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.chiropractorMaximum, {
      chiropractorMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.chiropodistMaximum, {
      chiropodistMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.massageMaximum, {
      massageMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.naturopathMaximum, {
      naturopathMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.osteopathMaximum, {
      osteopathMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.occupationalTherapistMaximum, {
      occupationalTherapistMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.physiotherapyMaximum, {
      physiotherapyMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.podiatristMaximum, {
      podiatristMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.psychologistMaximum, {
      psychologistMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.psychotherapistMaximum, {
      psychotherapistMaximum: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.registeredDieticianMaximum, {
      registeredDieticianMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.socialWorkerMaximum, {
      socialWorkerMax: helpers.formatNumber(data.paramedicalMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.speechTherapyMaximum, {
      speechTherapyMax: helpers.formatNumber(data.paramedicalMaximum)
    })));
  }
  if (data.paramedicalPerPractitionerToCombinedMaximumSelected && data.paramedicalSetPerPractitionerSelected) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.paramedicalCombinedMaximum, {
      paramedicalCombinedMaximum: helpers.formatNumber(data.paramedicalCombinedMaximum, 2, false)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.acupuncturistMaximum, {
      acupuncturistMax: helpers.formatNumber(data.acupuncturistMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.audiologistMaximum, {
      audiologistMax: helpers.formatNumber(data.audiologistMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.chiropractorMaximum, {
      chiropractorMax: helpers.formatNumber(data.chiropractorMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.chiropodistMaximum, {
      chiropodistMax: helpers.formatNumber(data.chiropodistMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.massageMaximum, {
      massageMax: helpers.formatNumber(data.massageMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.naturopathMaximum, {
      naturopathMax: helpers.formatNumber(data.naturopathMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.osteopathMaximum, {
      osteopathMax: helpers.formatNumber(data.osteopathMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.occupationalTherapistMaximum, {
      occupationalTherapistMax: helpers.formatNumber(data.occupationalTherapistMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.physiotherapyMaximum, {
      physiotherapyMax: helpers.formatNumber(data.physiotherapyMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.podiatristMaximum, {
      podiatristMax: helpers.formatNumber(data.podiatristMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.psychologistMaximum, {
      psychologistMax: helpers.formatNumber(data.psychologistMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.psychotherapistMaximum, {
      psychotherapistMaximum: helpers.formatNumber(data.psychotherapistMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.registeredDieticianMaximum, {
      registeredDieticianMax: helpers.formatNumber(data.registeredDieticianMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.socialWorkerMaximum, {
      socialWorkerMax: helpers.formatNumber(data.socialWorkerMaximum)
    })), /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "5px 0px 0px 0px"
    }, i18n.t(tokens.labels.coverage.speechTherapyMaximum, {
      speechTherapyMax: helpers.formatNumber(data.speechTherapyMaximum)
    })));
  }
  return null;
}

/**
 * @param data
 * @param type
 * @returns {{}}
 * @private
 */
function _buildCoverage(data, type) {
  if (!data.isSelected) {
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
      align: "center",
      mediumLarge: true
    }, "N/A"));
  }
  switch (type) {
    case 'lifeInsurance':
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, data.isFlatAmount ? `$${helpers.formatNumber(data.flatAmount)}` : data.timesEarnings), /*#__PURE__*/React.createElement(Text, {
        align: "center",
        medium: true
      }, data.isFlatAmount ? 'Maximum' : i18n.t(tokens.labels.planDesign.timesEarnings)));
    case 'deathDismemberment':
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, data.isFlatAmount ? `$${helpers.formatNumber(data.flatAmount)}` : data.timesEarnings), /*#__PURE__*/React.createElement(Text, {
        align: "center",
        medium: true
      }, data.isFlatAmount ? 'Maximum' : i18n.t(tokens.labels.planDesign.timesEarnings)));
    case 'dependentLife':
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, `$${helpers.formatNumber(data.spouseCoverage)}`), /*#__PURE__*/React.createElement(Text, {
        align: "center",
        medium: true
      }, i18n.t(tokens.labels.coverage.spouseMaximum)));
    case 'ltd':
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, data.isFlatAmount ? i18n.t(tokens.labels.planDesign.flatAmount) : i18n.t(tokens.labels.proofOfInsurance.graded)));
    case 'std':
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, data.isFlatAmount ? i18n.t(tokens.labels.planDesign.flatAmount) : i18n.t(tokens.labels.proofOfInsurance.graded)));
    case 'criticalIllness':
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, `$${helpers.formatNumber(data.flatAmount)}`), /*#__PURE__*/React.createElement(Text, {
        align: "center",
        medium: true
      }, "Maximum"));
    case 'dental':
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, `${data.basicReimbursement}%`), /*#__PURE__*/React.createElement(Text, {
        align: "center",
        medium: true
      }, i18n.t(tokens.labels.coverage.routineCareReimbursementLabel)));
    case 'drugs':
      return /*#__PURE__*/React.createElement("div", null, data.isGraded ? /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, `${data.firstPercentage}%`) : /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, `${data.reimbursement}%`), /*#__PURE__*/React.createElement(Text, {
        align: "center",
        medium: true
      }, i18n.t(tokens.titles.coverage)));
    case 'healthcare':
      return /*#__PURE__*/React.createElement("div", null, (data.paramedicalPerPractitionerSelected && data.paramedicalMaximum > 0 || data.paramedicalCombinedMaximumSelected && data.paramedicalCombinedMaximum > 0) && data.paramedicalReimbursement > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, `${data.paramedicalReimbursement}%`), /*#__PURE__*/React.createElement(Text, {
        align: "center",
        medium: true
      }, i18n.t(tokens.labels.coverage.paramedicalCoverageLabel))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, `${data.majorHealthReimbursement}%`), /*#__PURE__*/React.createElement(Text, {
        align: "center",
        medium: true
      }, i18n.t(tokens.labels.coverage.majorMedicalCoverage))));
    case 'outOfCountry':
      return /*#__PURE__*/React.createElement("div", null, data.isSelected ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, `$${helpers.formatNumber(data.outOfCountryMax)}`), /*#__PURE__*/React.createElement(Text, {
        align: "center",
        medium: true
      }, "Maximum")) : /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, "N/A"));
    case 'spendingAccounts':
      // eslint-disable-next-line no-case-declarations
      let coverageType = 'N/A';
      if (data.isPooled) {
        coverageType = i18n.t(tokens.labels.coverage.flex);
      } else if (data.includeHsa && data.includeLsa) {
        coverageType = i18n.t(tokens.labels.coverage.healthAndLifestyleIncluded);
      } else if (data.includeHsa) {
        coverageType = i18n.t(tokens.labels.coverage.healthOnly);
      } else if (data.includeLsa) {
        coverageType = i18n.t(tokens.labels.coverage.lifestyleOnly);
      }
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
        align: "center",
        mediumLarge: true
      }, coverageType), /*#__PURE__*/React.createElement(Text, {
        align: "center",
        medium: true
      }, i18n.t(tokens.labels.planDesign.coverage)));
    default:
      return null;
  }
}

/**
 * @param data
 * @param type
 * @private
 */
function _buildDetails(data, type, isStudent) {
  if (!data.isSelected) {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        whiteSpace: 'pre-line',
        maxHeight: 350,
        overflow: 'auto'
      }
    }, /*#__PURE__*/React.createElement(Text, {
      align: "center",
      mediumLarge: true
    }, "N/A"));
  }
  switch (type) {
    case 'lifeInsurance':
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, data.isFlatAmount ? i18n.t(tokens.labels.coverage.flatAmountPlanCoverage, {
        flatAmount: helpers.formatNumber(data.flatAmount)
      }) : i18n.t(tokens.labels.coverage.timesEarnings, {
        timesEarnings: data.timesEarnings
      })), data.maxEvidence > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxWithEvidence, {
        maxEvidence: helpers.formatNumber(data.maxEvidence)
      })) : null, data.maxNoEvidence > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxNoEvidence, {
        maxNoEvidence: helpers.formatNumber(data.maxNoEvidence)
      })) : null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.coverageReducesAtAge, {
        reducesBy: data.reducesBy,
        reducesAtAge: data.reducesAtAge
      })), data.includeSecondReduction ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.furtherCoverageReducesAtAge, {
        furtherReducesBy: data.furtherReducesBy,
        furtherReductionAge: data.furtherReductionAge
      })) : null, data.earlyPaymentPrivilege ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, localizationMaps.fromEnglish("earlyPaymentPrivilegeType", data.earlyPaymentPrivilegeType)) : null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.monthWaitingPeriod, {
        eligibilityPeriod: data.eligibilityPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.minimumHoursPerWeekRequired, {
        minHoursPerWeek: data.minHoursPerWeek
      })), isStudent ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtEndOfStudy, {
        terminationAge: data.terminationAge
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtAge, {
        terminationAge: data.terminationAge
      })));
    case 'deathDismemberment':
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, data.isFlatAmount ? i18n.t(tokens.labels.coverage.flatAmountPlanCoverage, {
        flatAmount: helpers.formatNumber(data.flatAmount)
      }) : i18n.t(tokens.labels.coverage.timesEarnings, {
        timesEarnings: data.timesEarnings
      })), data.maxEvidence > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxWithEvidence, {
        maxEvidence: helpers.formatNumber(data.maxEvidence)
      })) : null, data.maxNoEvidence > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxNoEvidence, {
        maxNoEvidence: helpers.formatNumber(data.maxNoEvidence)
      })) : null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.coverageReducesAtAge, {
        reducesBy: data.reducesBy,
        reducesAtAge: data.reducesAtAge
      })), data.includeSecondReduction ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.furtherCoverageReducesAtAge, {
        furtherReducesBy: data.furtherReducesBy,
        furtherReductionAge: data.furtherReductionAge
      })) : null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.monthWaitingPeriod, {
        eligibilityPeriod: data.eligibilityPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.minimumHoursPerWeekRequired, {
        minHoursPerWeek: data.minHoursPerWeek
      })), isStudent ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtEndOfStudy, {
        terminationAge: data.terminationAge
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtAge, {
        terminationAge: data.terminationAge
      })));
    case 'dependentLife':
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maximumForSpouse, {
        spouseCoverage: helpers.formatNumber(data.spouseCoverage)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maximumForChildren, {
        childCoverage: helpers.formatNumber(data.childCoverage)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.monthWaitingPeriod, {
        eligibilityPeriod: data.eligibilityPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.minimumHoursPerWeekRequired, {
        minHoursPerWeek: data.minHoursPerWeek
      })), isStudent ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtEndOfStudy, {
        terminationAge: data.terminationAge
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtAge, {
        terminationAge: data.terminationAge
      })));
    case 'ltd':
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, data.isGraded ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        bold: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.titles.gradedCoverage)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, createGradedLTDCoverage(data)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "0px 0px 20px 0px"
      }, data.isTaxable ? i18n.t(tokens.labels.coverage.taxable) : i18n.t(tokens.labels.coverage.nonTaxable))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        bold: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.titles.flatAmountCoverage)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.percentageOfFlatAmount, {
        percentageOfFlatAmount: data.percentageOfFlatAmount
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "0px 0px 20px 0px"
      }, data.isTaxable ? i18n.t(tokens.labels.coverage.taxable) : i18n.t(tokens.labels.coverage.nonTaxable))), data.maxEvidence > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxWithEvidence, {
        maxEvidence: helpers.formatNumber(data.maxEvidence)
      })) : null, data.maxNoEvidence > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxNoEvidence, {
        maxNoEvidence: helpers.formatNumber(data.maxNoEvidence)
      })) : null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.dayEliminationPeriod, {
        elimPeriod: data.elimPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, localizationMaps.fromEnglish("defOfDisability", data.defOfDisability)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.benefitsPeriod, {
        benefitsPeriod: localizationMaps.fromEnglish("benefitsPeriod", data.benefitsPeriod)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.monthWaitingPeriod, {
        eligibilityPeriod: data.eligibilityPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.minimumHoursPerWeekRequired, {
        minHoursPerWeek: data.minHoursPerWeek
      })), isStudent ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtEndOfStudy, {
        terminationAge: data.terminationAge
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtAge, {
        terminationAge: data.terminationAge
      })), data.includeCostOfLivingAdjustments && data.costOfLivingAdjustmentsPercentage ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.costOfLivingAdjToDisability, {
        costOfLivingAdjustmentsPercentage: data.costOfLivingAdjustmentsPercentage
      })) : null, data.includeAllowPreExistingConditions ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.allowPreexistingConditions)) : null, data.includeLumpSumPaymentDisabledParticipantsDeath ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.lumpSumPayment)) : null);
    case 'std':
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, data.isGraded ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        bold: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.titles.gradedCoverage)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, createGradedSTDCoverage(data)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "0px 0px 20px 0px"
      }, data.isTaxable ? i18n.t(tokens.labels.coverage.taxable) : i18n.t(tokens.labels.coverage.nonTaxable))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        bold: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.titles.flatAmountCoverage)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.percentageOfFlatAmountOfWeeklyEarnings, {
        percentageOfFlatAmount: data.percentageOfFlatAmount
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, data.isTaxable ? i18n.t(tokens.labels.coverage.taxable) : i18n.t(tokens.labels.coverage.nonTaxable))), data.maxEvidence > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxWithEvidence, {
        maxEvidence: helpers.formatNumber(data.maxEvidence)
      })) : null, data.maxNoEvidence > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxNoEvidence, {
        maxNoEvidence: helpers.formatNumber(data.maxNoEvidence)
      })) : null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.elimPeriodAccident, {
        elimPeriodAccident: data.elimPeriodAccident
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.elimPeriodHospital, {
        elimPeriodHospital: data.elimPeriodHospital
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.elimPeriodSickness, {
        elimPeriodSickness: data.elimPeriodSickness
      })), data.defOfDisability ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, localizationMaps.fromEnglish("defOfDisability", data.defOfDisability)) : null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.benefitsPeriodWeek, {
        benefitsPeriod: data.benefitsPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.monthWaitingPeriod, {
        eligibilityPeriod: data.eligibilityPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.minimumHoursPerWeekRequired, {
        minHoursPerWeek: data.minHoursPerWeek
      })), isStudent ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtEndOfStudy, {
        terminationAge: data.terminationAge
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtAge, {
        terminationAge: data.terminationAge
      })));
    case 'criticalIllness':
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.flatAmountPlanCoverage, {
        flatAmount: helpers.formatNumber(data.flatAmount)
      })), data.maxEvidence > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxWithEvidence, {
        maxEvidence: helpers.formatNumber(data.maxEvidence)
      })) : null, data.maxNoEvidence > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxNoEvidence, {
        maxNoEvidence: helpers.formatNumber(data.maxNoEvidence)
      })) : null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.coverageReducesAtAge, {
        reducesBy: data.reducesBy,
        reducesAtAge: data.reducesAtAge
      })), data.includeSecondReduction ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.furtherCoverageReducesAtAge, {
        furtherReducesBy: data.furtherReducesBy,
        furtherReductionAge: data.furtherReductionAge
      })) : null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.monthWaitingPeriod, {
        eligibilityPeriod: data.eligibilityPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.minimumHoursPerWeekRequired, {
        minHoursPerWeek: data.minHoursPerWeek
      })), isStudent ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtEndOfStudy, {
        terminationAge: data.terminationAge
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtAge, {
        terminationAge: data.terminationAge
      })));
    case 'dental':
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, data.combineBasicAndMajor ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.combinedMax, {
        amount: helpers.formatNumber(data.basicMajorCombinedMax)
      })) : /*#__PURE__*/React.createElement(React.Fragment, null, data.includeMajor ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.majorMaxAmount, {
        amount: helpers.formatNumber(data.majorMax)
      })) : null, data.includeBasic ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.basicMaxAmount, {
        amount: helpers.formatNumber(data.basicMax)
      })) : null), data.includeBasic ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.routineCareReimbursement, {
        routineCareReimbursement: data.basicReimbursement
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.endodonticsReimbursement, {
        endodonticsReimbursement: data.basicReimbursement
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.periodonticsReimbursement, {
        periodonticsReimbursement: data.basicReimbursement
      }))) : null, data.includeMajor ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.bridgeWorkReimbursement, {
        bridgeWorkReimbursement: data.majorReimbursement
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.crownsReimbursement, {
        crownsReimbursement: data.majorReimbursement
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.denturesReimbursement, {
        denturesReimbursement: data.majorReimbursement
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.dentureRepairReimbursement, {
        dentureRepairReimbursement: data.majorReimbursement
      }))) : null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.unitsOfScaling, {
        unitsOfScaling: data.unitsOfScaling
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.dentalRecall, {
        monthsToRecall: data.monthsToRecall
      })), data.hasDeductible && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.deductibleForSoloCoverage, {
        soloDeductibleAmount: helpers.formatNumber(data.soloDeductibleAmount)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.deductibleForDuoCoverage, {
        duoDeductibleAmount: helpers.formatNumber(data.duoDeductibleAmount)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.deductibleForFamilyCoverage, {
        familyDeductibleAmount: helpers.formatNumber(data.familyDeductibleAmount)
      }))), data.includeOrthodontics && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.orthoLifetimeMax, {
        orthodonticLifetimeMax: helpers.formatNumber(data.orthodonticLifetimeMax)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.orthodonticReimbursement, {
        orthodonticReimbursement: data.orthodonticReimbursement
      })), isStudent ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.orthodonticsTerminationStudent, {
        orthodonticTerminationAge: data.orthodonticTerminationAge
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.orthodonticsTermination, {
        orthodonticTerminationAge: data.orthodonticTerminationAge
      }))), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.monthsSurvivorBenefit, {
        monthsSurvivorBenefit: data.monthsSurvivorBenefit
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.monthWaitingPeriod, {
        eligibilityPeriod: data.eligibilityPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.minimumHoursPerWeekRequired, {
        minHoursPerWeek: data.minHoursPerWeek
      })), isStudent ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtEndOfStudy, {
        terminationAge: data.terminationAge
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtAge, {
        terminationAge: data.terminationAge
      })));
    case 'drugs':
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, data.isGraded ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.gradedPercentageString, {
        firstPercentage: data.firstPercentage,
        firstMax: data.firstMax,
        secondPercentage: data.secondPercentage,
        secondMax: data.secondMax
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.reimbursementOf, {
        reimbursement: data.reimbursement
      })), data.hasOverallMax && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.overallMaximum, {
        overallMax: helpers.formatNumber(data.overallMax)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.genericSubstitute, {
        genericSubstitute: localizationMaps.fromEnglish("genericSubstitute", data.genericSubstitute)
      })), data.isPreferredProvider && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.preferredProvider, {
        preferredProvider: data.preferredProvider
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.preferredProviderReimbursement, {
        preferredProviderReimbursement: data.preferredProviderReimbursement
      }))), data.hasDeductible && data.deductibleAmountSelected && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.deductibleForSoloCoverage, {
        soloDeductibleAmount: helpers.formatNumber(data.soloDeductibleAmount)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.deductibleForDuoCoverage, {
        duoDeductibleAmount: helpers.formatNumber(data.duoDeductibleAmount)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.deductibleForFamilyCoverage, {
        familyDeductibleAmount: helpers.formatNumber(data.familyDeductibleAmount)
      }))), data.hasDeductible && data.deductibleEqualToDispenseFee && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.deductibleEqualToDispenseFee)), data.hasDispenseFeeMax && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.dispenseFeeMaximum, {
        dispenseFeeMax: helpers.formatNumber(data.dispenseFeeMax)
      })), data.includeErectDysfuncDrugs && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.erectileDysfunctionMaximum, {
        edMax: helpers.formatNumber(data.edMax)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.erectileDysfunctionReimbursement, {
        edReimbursement: data.edReimbursement
      }))), data.includeFertilityDrugs && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.fertilityDrugsMaximum, {
        fertilityMax: helpers.formatNumber(data.fertilityMax)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.fertilityDrugsReimbursement, {
        fertilityReimbursement: data.fertilityReimbursement
      }))), data.includePreventativeVaccines ? /*#__PURE__*/React.createElement(React.Fragment, null, data.preventativeVaccinesIsRC ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.customaryExpensesForPreventativeVaccines)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.preventativeVaccinesReimbursement, {
        preventativeVaccinesReimbursement: data.preventativeVaccinesReimbursement
      }))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.preventativeVaccinesMaximum, {
        preventativeVaccinesMax: helpers.formatNumber(data.preventativeVaccinesMax)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.preventativeVaccinesReimbursement, {
        preventativeVaccinesReimbursement: data.preventativeVaccinesReimbursement
      })))) : null, data.includeSmokingCessations && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.smokingCessationMaximum, {
        smokingCessationsMax: helpers.formatNumber(data.smokingCessationsMax)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.smokingCessationReimbursement, {
        smokingCessationsReimbursement: data.smokingCessationsReimbursement
      }))), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.monthWaitingPeriod, {
        eligibilityPeriod: data.eligibilityPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.minimumHoursPerWeekRequired, {
        minHoursPerWeek: data.minHoursPerWeek
      })), isStudent ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtEndOfStudy, {
        terminationAge: data.terminationAge
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtAge, {
        terminationAge: data.terminationAge
      })));
    case 'healthcare':
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.majorHealthReimbursement, {
        majorHealthReimbursement: data.majorHealthReimbursement
      })), data.majorHealthCombinedMaximumSelected ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.majorHealthCombinedMaximum, {
        majorHealthCombinedMaximum: helpers.formatNumber(data.majorHealthCombinedMaximum)
      })) : null, data.cataractSurgeryEyewear !== 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.cataractSurgeryEyewear, {
        cataractSurgeryEyewear: helpers.formatNumber(data.cataractSurgeryEyewear),
        cataractSurgeryEyewearPeriod: data.cataractSurgeryEyewearPeriod
      })) : null, data.includeConvalescentHospital > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.convalescentHospital, {
        convalescentHospitalReimbursement: data.convalescentHospitalReimbursement,
        convalescentHospitalDailyMax: helpers.formatNumber(data.convalescentHospitalDailyMax),
        convalescentHospitalMaxDays: data.convalescentHospitalMaxDays
      })) : null, data.includeMedicalReferral ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.medicalReferral, {
        medicalReferralReimbursement: data.medicalReferralReimbursement,
        medicalReferralMax: helpers.formatNumber(data.medicalReferralMax)
      })) : null, data.includeDiagnosticServices ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.diagnosticServices, {
        diagnosticServicesReimbursement: data.diagnosticServicesReimbursement,
        diagnosticServicesMax: helpers.formatNumber(data.diagnosticServicesMax)
      })) : null, data.includeLaserEyeSurgery ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.laserEye, {
        laserEyeSurgeryReimbursement: helpers.formatNumber(data.laserEyeSurgeryReimbursement),
        laserEyeSurgeryMaximum: helpers.formatNumber(data.laserEyeSurgeryMaximum, 2, true)
      })) : null, data.includeGenderAffirmation ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.genderAffirmationCoverage, {
        genderAffirmationReimbursement: helpers.formatNumber(data.genderAffirmationReimbursement),
        genderAffirmationMax: helpers.formatNumber(data.genderAffirmationMax, 2, true)
      })) : null, data.dentalAccidentMax > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.dentalAccidentMax, {
        dentalAccidentMax: helpers.formatNumber(data.dentalAccidentMax)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.dentalAccidentReimbursement, {
        dentalAccidentReimbursement: data.dentalAccidentReimbursement
      }))) : null, data.apneaMachine > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.apneaMachineAmount, {
        apneaMachine: helpers.formatNumber(data.apneaMachine)
      })) : null, data.insulinPump > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.insulinPumpAmount, {
        insulinPump: helpers.formatNumber(data.insulinPump)
      })) : null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.artificialEyeLimbPerLifetime)), data.hearingAids > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.hearingAidsMonths, {
        hearingAids: helpers.formatNumber(data.hearingAids),
        hearingAidsPeriod: data.hearingAidsPeriod
      })) : null, data.bloodPressureMonitor > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.bloodPressureMonitor, {
        bloodPressureMonitor: helpers.formatNumber(data.bloodPressureMonitor)
      })) : null, data.compressionStockings > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.compressionStockings, {
        compressionStockings: helpers.formatNumber(data.compressionStockings)
      })) : null, data.insulinPumpSupplies > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.insulinPumpSupplies, {
        insulinPumpSupplies: helpers.formatNumber(data.insulinPumpSupplies)
      })) : null, data.artificialEyeLimbRepairReplacement > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.artificialEyeLimbRepairReplacement, {
        artificialEyeLimbRepairReplacement: helpers.formatNumber(data.artificialEyeLimbRepairReplacement)
      })) : null, data.glucoseMonitoringEquipmentSupplies > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.glucoseMonitoringEquipmentSupplies, {
        glucoseMonitoringEquipmentSupplies: helpers.formatNumber(data.glucoseMonitoringEquipmentSupplies)
      })) : null, data.tensMachine > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.tensMachine, {
        tensMachine: helpers.formatNumber(data.tensMachine)
      })) : null, data.viscosupplementation > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.viscosupplementation, {
        viscosupplementation: helpers.formatNumber(data.viscosupplementation)
      })) : null, data.wheelchairManual > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.wheelchairManual, {
        wheelchairManual: helpers.formatNumber(data.wheelchairManual)
      })) : null, data.wheelchairElectric > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.wheelchairElectric, {
        wheelchairElectric: helpers.formatNumber(data.wheelchairElectric)
      })) : null, data.wigs > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.wigs, {
        wigs: helpers.formatNumber(data.wigs)
      })) : null, data.cardiacRehabilitation > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.cardiacRehabilitation, {
        cardiacRehabilitation: helpers.formatNumber(data.cardiacRehabilitation)
      })) : null, data.footOrthotics > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.footOrthotics, {
        footOrthotics: helpers.formatNumber(data.footOrthotics)
      })) : null, data.includeOrthopedicShoes ? /*#__PURE__*/React.createElement(React.Fragment, null, data.orthopedicShoesMaximumType === 'dollar' ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.orthopedicShoes, {
        orthopedicShoes: helpers.formatNumber(data.orthopedicShoesMaximum)
      })) :
      /*#__PURE__*/
      // pairs
      React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.orthopedicShoesPair, {
        orthopedicShoes: data.orthopedicShoesPairsPerYear > 1 ? `${i18n.t(tokens.labels.coverage.pairsLimit, {
          limit: data.orthopedicShoesPairsPerYear
        })}` : `${i18n.t(tokens.labels.coverage.pairLimit, {
          limit: data.orthopedicShoesPairsPerYear
        })}`
      }))) : null, data.privateDutyNursing > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.privateDutyNursing, {
        privateDutyNursing: helpers.formatNumber(data.privateDutyNursing)
      })) : null, data.prosthetics > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.prosthetics, {
        prosthetics: helpers.formatNumber(data.prosthetics)
      })) : null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.reasonableAndCustomaryExpenses)), /*#__PURE__*/React.createElement("div", {
        style: {
          paddingLeft: "15px"
        }
      }, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.ambulanceServices)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.hospital)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.medicalEquipment)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.medicalServicesAndSupplies)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.apneaMachineSupplies)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.apneaMask)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.bracesWithRigidSupports)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.crutches)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.ippBreathingMachine)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.ostomySupplies)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.surgicalBra))), data.includeEmployeeAssistance && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.employeeFamilyAssistanceProgram)), data.includeMedicalSecondOpinion && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.medicalSecondOpinionCoverage)), data.includeEldercare && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.eldercareCoverage)), data.includeMentalHealthOnDemand && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.mentalHealthOnDemandCoverage))), data.includeWello && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, "Maple"), data.paramedicalReimbursement > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.paramedicalReimbursement, {
        paramedicalReimbursement: data.paramedicalReimbursement
      })), paramedicalOptions(data)) : null, data.familyCombinedMaximumSelected ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.combinedFamilyMaximumPlanOverview, {
        combinedMaximum: helpers.formatNumber(data.familyCombinedMaximum)
      })) : null, data.paramedicalGroupMentalWellnessSelected ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.mentalWellnessReimbursement, {
        mentalWellnessReimbursement: data.mentalWellnessReimbursement
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.mentalWellnessMaximum, {
        mentalWellnessMaximum: helpers.formatNumber(data.mentalWellnessMaximum)
      })), /*#__PURE__*/React.createElement("div", {
        style: {
          paddingLeft: "15px"
        }
      }, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.dropdowns.benefits.psychologist)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.dropdowns.benefits.psychotherapist), " "), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.dropdowns.benefits.socialWorker), " "))) : null, data.paramedicalGroupFunctionalTreatmentSelected ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.planDesign.functionalTreatmentReimbursement), " ", data.functionalTreatmentReimbursement, "%"), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.planDesign.functionalTreatmentMaximum), " $", helpers.formatNumber(data.functionalTreatmentMaximum)), /*#__PURE__*/React.createElement("div", {
        style: {
          paddingLeft: "15px"
        }
      }, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.dropdowns.benefits.osteopath)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.dropdowns.benefits.chiropodist), " "), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.dropdowns.benefits.podiatrist), " "))) : null, data.visionPerPractitionerSelected ? /*#__PURE__*/React.createElement(React.Fragment, null, data.includeEyeExams && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.eyeExamReimbursementMax, {
        eyeExamReimbursement: data.eyeExamReimbursement
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.eyeExamAdultMax, {
        eyeExamAdultMax: helpers.formatNumber(data.eyeExamAdultMax),
        eyeExamRolloverPeriodAdult: data.eyeExamRolloverPeriodAdult
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.eyeExamChildMax, {
        eyeExamChildMax: helpers.formatNumber(data.eyeExamChildMax),
        eyeExamRolloverPeriodChild: data.eyeExamRolloverPeriodChild
      }))), data.includeEyeGlassesAndContacts && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.eyeGlassesAndContactsReimbursement, {
        eyeGlassesAndContactsReimbursement: data.eyeGlassesAndContactsReimbursement
      })), data.eyeGlassesAndContactsAdultMax > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.eyeGlassesAndContactsAdultMax, {
        eyeGlassesAndContactsAdultMax: helpers.formatNumber(data.eyeGlassesAndContactsAdultMax),
        eyeGlassesAndContactsPeriodAdult: data.eyeGlassesAndContactsPeriodAdult
      })) : null, data.eyeGlassesAndContactsChildMax > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.eyeGlassesAndContactsChildMax, {
        eyeGlassesAndContactsChildMax: helpers.formatNumber(data.eyeGlassesAndContactsChildMax),
        eyeGlassesAndContactsPeriodChild: data.eyeGlassesAndContactsPeriodChild
      })) : null), data.includeRequiredSafetyEyeWear && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.requiredSafetyEyeWearReimbursement, {
        requiredSafetyEyeWearReimbursement: data.requiredSafetyEyeWearReimbursement
      })), data.requiredSafetyEyeWearAdultMax > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.requiredSafetyEyeWearAdultMax, {
        requiredSafetyEyeWearAdultMax: helpers.formatNumber(data.requiredSafetyEyeWearAdultMax),
        requiredSafetyEyeWearPeriodAdult: data.requiredSafetyEyeWearPeriodAdult
      })) : null, data.requiredSafetyEyeWearChildMax > 0 ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.requiredSafetyEyeWearChildMax, {
        requiredSafetyEyeWearChildMax: helpers.formatNumber(data.requiredSafetyEyeWearChildMax),
        requiredSafetyEyeWearPeriodChild: data.requiredSafetyEyeWearPeriodChild
      })) : null)) : null, data.visionCombinedMaximumSelected ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.planCoverageVisionCombinedReimbursement, {
        visionCombinedReimbursement: data.visionCombinedReimbursement
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.planCoverageVisionCombinedMaximumAdult, {
        visionCombinedMax: helpers.formatNumber(data.visionCombinedAdultMax)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.planCoverageVisionPeriodAdult, {
        visionPeriod: data.visionCombinedAdultMaxPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.planCoverageVisionCombinedMaximumChild, {
        visionCombinedMax: helpers.formatNumber(data.visionCombinedChildMax)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.planCoverageVisionPeriodChild, {
        visionPeriod: data.visionCombinedChildMaxPeriod
      }))) : null, data.hasDeductible && /*#__PURE__*/React.createElement(React.Fragment, null, data.soloDeductibleAmount ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.deductibleForSoloCoverage, {
        soloDeductibleAmount: helpers.formatNumber(data.soloDeductibleAmount)
      })) : null, data.duoDeductibleAmount ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.deductibleForDuoCoverage, {
        duoDeductibleAmount: helpers.formatNumber(data.duoDeductibleAmount)
      })) : null, data.familyDeductibleAmount ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.deductibleForFamilyCoverage, {
        familyDeductibleAmount: helpers.formatNumber(data.familyDeductibleAmount)
      })) : null), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.monthWaitingPeriod, {
        eligibilityPeriod: data.eligibilityPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.minimumHoursPerWeekRequired, {
        minHoursPerWeek: data.minHoursPerWeek
      })), isStudent ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtEndOfStudy, {
        terminationAge: data.terminationAge
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtAge, {
        terminationAge: data.terminationAge
      })));
    case 'outOfCountry':
      if (!data.isSelected) {
        return /*#__PURE__*/React.createElement(Text, {
          align: "center",
          mediumLarge: true
        }, "N/A");
      }
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, data.outOfCountryMax && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.outOfCountryProvinceMaximum, {
        outOfCountryMax: helpers.formatNumber(data.outOfCountryMax)
      })), data.outOfCountryPeriod30 && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.outOfCountryProvincePeriod30Days)), data.outOfCountryPeriod60 && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.outOfCountryProvincePeriod60Days)), data.outOfCountryPeriod90 && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.outOfCountryProvincePeriod90Days)), data.outOfCountryPeriod120 && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.outOfCountryProvincePeriod120Days)), data.outOfCountryPeriod180 && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.outOfCountryProvincePeriod180Days)), data.outOfCountryIsGraded && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.tripDurationReduce, {
        outOfCountryPeriodAfterReduction: data.outOfCountryPeriodAfterReduction,
        outOfCountryReducesAtAge: data.outOfCountryReducesAtAge
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.coverageReducesAtAge, {
        reducesBy: data.outOfCountryReducesBy,
        reducesAtAge: data.outOfCountryReducesAtAge
      }))), data.includeOutOfCountryMedicalReferral && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.outOfCountryMedicalReferralMax, {
        outOfCountryMedicalReferralMax: helpers.formatNumber(data.outOfCountryMedicalReferralMax)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.outOfCountryMedicalReferralPerYearReimbursement, {
        outOfCountryMedicalReferralPerYearReimbursement: data.outOfCountryMedicalReferralPerYearReimbursement
      }))), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.monthWaitingPeriod, {
        eligibilityPeriod: data.eligibilityPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.minimumHoursPerWeekRequired, {
        minHoursPerWeek: data.minHoursPerWeek
      })), isStudent ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtEndOfStudy, {
        terminationAge: data.terminationAge
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtAge, {
        terminationAge: data.terminationAge
      })));
    case 'efap':
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, /*#__PURE__*/React.createElement(React.Fragment, null, data.includeEmployeeAssistance && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.employeeFamilyAssistanceProgram)), data.includeMedicalSecondOpinion && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.medicalSecondOpinionCoverage)), data.includeEldercare && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.eldercareCoverage)), data.includeMentalHealthOnDemand && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.mentalHealthOnDemandCoverage))));
    case 'virtualHealthcare':
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, data.includeMaple && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, "Maple"), data.includeGotodoctor && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, "Gotodoctor"), data.includeDialogue && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, "Dialogue"));
    case 'financialAwareness':
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, data.includeEnrichedAcademy && /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, "Enriched Academy"));
    case 'spendingAccounts':
      // eslint-disable-next-line no-case-declarations
      let coverageType = 'N/A';
      if (data.includeHsa && data.includeLsa) {
        coverageType = i18n.t(tokens.labels.coverage.healthAndLifestyleIncluded);
      } else if (data.includeHsa) {
        coverageType = i18n.t(tokens.labels.coverage.healthOnly);
      } else if (data.includeLsa) {
        coverageType = i18n.t(tokens.labels.coverage.lifestyleOnly);
      }
      return /*#__PURE__*/React.createElement("div", {
        style: {
          whiteSpace: 'pre-line',
          maxHeight: 350,
          overflow: 'auto'
        }
      }, data.isPooled ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.memberSetsPooledAmount)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.totalAvailableAmount, {
        pooledAmountAvailable: helpers.formatNumber(data.pooledAmountAvailable)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, data.allowPooledAmountRollover === 'Yes' ? i18n.t(tokens.labels.coverage.rolloverEnabled) : i18n.t(tokens.labels.coverage.noRollover)), data.allowPooledAmountRollover === 'Yes' && data.setMaxPooledRollover === 'Yes' && /*#__PURE__*/React.createElement(React.Fragment, null, data.setMaxHsaRollover === 'Yes' ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxHealthRollover, {
        maxHsaRollover: helpers.formatNumber(data.maxHsaRollover)
      })) : null, data.setMaxLsaRollover === 'Yes' ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxLifeRollover, {
        maxLsaRollover: helpers.formatNumber(data.maxLsaRollover)
      })) : null)) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, coverageType), data.includeHsa && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.healthSpendingMax, {
        hsaAmount: helpers.formatNumber(data.hsaAmount)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, data.allowHsaRollover === 'Yes' ? i18n.t(tokens.labels.coverage.hsaRolloverEnabled) : i18n.t(tokens.labels.coverage.noHsaRolloverEnabled)), data.allowHsaRollover === 'Yes' && data.setMaxHsaRollover === 'Yes' ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxHealthRollover, {
        maxHsaRollover: helpers.formatNumber(data.maxHsaRollover)
      })) : null), data.includeLsa && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.lifestyleSpendingMax, {
        lsaAmount: helpers.formatNumber(data.lsaAmount)
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, data.allowLsaRollover === 'Yes' ? i18n.t(tokens.labels.coverage.lsaRolloverEnabled) : i18n.t(tokens.labels.coverage.noLsaRolloverEnabled)), data.allowLsaRollover === 'Yes' && data.setMaxLsaRollover === 'Yes' ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.maxLifeRollover, {
        maxLsaRollover: helpers.formatNumber(data.maxLsaRollover)
      })) : null)), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.monthWaitingPeriod, {
        eligibilityPeriod: data.eligibilityPeriod
      })), /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.minimumHoursPerWeekRequired, {
        minHoursPerWeek: data.minHoursPerWeek
      })), isStudent ? /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtEndOfStudy, {
        terminationAge: data.terminationAge
      })) : /*#__PURE__*/React.createElement(Text, {
        medium: true,
        margin: "5px 0px 0px 0px"
      }, i18n.t(tokens.labels.coverage.terminatesAtAge, {
        terminationAge: data.terminationAge
      })));
    default:
      return null;
  }
}

/**
 * @param data
 * @param type
 * @param classes
 * @returns {*}
 * @constructor
 */
const PlanDisplayBlock = ({
  data,
  type,
  isStudent = false
}) => {
  const styles = {
    tableHeader: {
      textAlign: 'center',
      backgroundColor: Theme.colors.backgroundGrey,
      borderBottom: `1px solid ${Theme.colors.lightGrey}`
    }
  };
  return /*#__PURE__*/React.createElement(Table, null, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, {
    size: "small",
    sx: {
      width: '20%',
      ...styles.tableHeader
    }
  }, /*#__PURE__*/React.createElement(Text, {
    medium: true,
    color: Theme.colors.mainTextGrey,
    align: "center"
  }, i18n.t(tokens.titles.class))), /*#__PURE__*/React.createElement(TableCell, {
    size: "small",
    style: {
      width: '30%',
      ...styles.tableHeader
    }
  }, /*#__PURE__*/React.createElement(Text, {
    medium: true,
    color: Theme.colors.mainTextGrey,
    align: "center"
  }, i18n.t(tokens.titles.coverage))), /*#__PURE__*/React.createElement(TableCell, {
    size: "small",
    style: {
      width: '50%',
      ...styles.tableHeader
    }
  }, /*#__PURE__*/React.createElement(Text, {
    medium: true,
    color: Theme.colors.mainTextGrey,
    align: "center"
  }, i18n.t(tokens.titles.details))))), /*#__PURE__*/React.createElement(TableBody, null, data.sort((a, b) => {
    // Compare the division names without spaces and all lowercase
    const aTitle = a.classTitle.toLowerCase().replace(/ /g, '');
    const bTitle = b.classTitle.toLowerCase().replace(/ /g, '');

    /*
     * First compare without numbers to see if the strings match. If they do match then sort by number
     * If the strings without numbers aren't the same then sort alphabetically
     */
    return aTitle.replace(/[0-9]/g, '') === bTitle.replace(/[0-9]/g, '') && Number(aTitle.replace(/^\D+/g, '')) - Number(bTitle.replace(/^\D+/g, '')) || aTitle.localeCompare(bTitle);
  }).map((item, index) => {
    const {
      classTitle,
      classDescription
    } = item;
    if (!item.coverage) {
      return null;
    }
    const coverage = _buildCoverage(item.coverage, type);
    const details = _buildDetails(item.coverage, type, isStudent);
    return /*#__PURE__*/React.createElement(TableRow, {
      sx: index % 2 !== 0 ? {
        backgroundColor: Theme.colors.backgroundGrey,
        border: "none"
      } : {
        border: "none"
      },
      key: `${classTitle}-${type}`
    }, /*#__PURE__*/React.createElement(TableCell, {
      style: {
        padding: 10,
        width: '20%',
        border: "none"
      }
    }, /*#__PURE__*/React.createElement(Tooltip, {
      tip: classDescription,
      placement: "top"
    }, /*#__PURE__*/React.createElement(Text, {
      mediumLarge: true,
      margin: "0 40px",
      customStyle: {
        width: '100%'
      }
    }, classTitle))), /*#__PURE__*/React.createElement(TableCell, {
      style: {
        padding: 10,
        width: '30%',
        border: "none"
      }
    }, coverage), /*#__PURE__*/React.createElement(TableCell, {
      style: {
        padding: 10,
        width: '50%',
        border: "none"
      }
    }, details));
  })));
};
export default PlanDisplayBlock;